import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, CheckCircle2, Clock, ShieldCheck, Sparkles } from 'lucide-react';
import { submitWaitlistApplication } from '../services/apiClient';

const initialForm = {
  firstName: '',
  lastName: '',
  email: '',
  city: '',
  state: '',
  investorType: 'individual_investor',
  investorExperience: 'beginner',
  bondKnowledge: 'novice',
  heardFrom: '',
  interestReason: '',
  marketingConsent: true,
  termsAccepted: false,
  educationAcknowledged: false,
  website: '',
};

export default function WaitlistPage({ onBackToLogin }) {
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const startedAtRef = useRef(null);
  useEffect(() => {
    startedAtRef.current = Date.now();
  }, []);

  function updateField(name, value) {
    setForm((current) => ({ ...current, [name]: value }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSubmitting(true);
    setStatus(null);

    const result = await submitWaitlistApplication({
      ...form,
      elapsedMs: Date.now() - (startedAtRef.current || Date.now()),
    });

    if (result?.ok) {
      setStatus({ type: 'success', message: result.message || 'You are on the Bond Mentor waitlist.' });
      setForm(initialForm);
    } else {
      setStatus({ type: 'error', message: result?.message || 'Waitlist request could not be submitted. Please try again.' });
    }
    setSubmitting(false);
  }

  return (
    <div className="min-h-screen bg-mentor-bg text-mentor-text flex items-center justify-center px-4 py-10">
      <div className="max-w-5xl w-full grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-6 items-stretch">
        <section className="glass-card p-8 border border-mentor-border/70 flex flex-col justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-mentor-accent/30 bg-mentor-accent/10 px-3 py-1 text-xs text-mentor-accent font-display font-bold uppercase tracking-wider mb-5">
              <Clock size={14} /> Soft Launch Waitlist
            </div>
            <h1 className="text-3xl sm:text-4xl font-display font-bold leading-tight mb-4">
              Join the Bond Mentor early-access list.
            </h1>
            <p className="text-mentor-muted leading-relaxed mb-6">
              Bond Mentor translates bond-market warnings into plain-English market awareness. During soft launch, we review applications before sending invite links so the dashboard stays safe, stable, and useful for serious learners.
            </p>
            <div className="space-y-4">
              <div className="flex gap-3">
                <ShieldCheck className="text-mentor-accent shrink-0 mt-0.5" size={20} />
                <div>
                  <h2 className="font-display font-bold">Controlled access</h2>
                  <p className="text-sm text-mentor-muted">Approved users receive a secure invitation link instead of open registration.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Sparkles className="text-mentor-accent shrink-0 mt-0.5" size={20} />
                <div>
                  <h2 className="font-display font-bold">Beginner-friendly review</h2>
                  <p className="text-sm text-mentor-muted">Your experience level helps us improve wording, examples, and onboarding.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <CheckCircle2 className="text-mentor-accent shrink-0 mt-0.5" size={20} />
                <div>
                  <h2 className="font-display font-bold">Educational only</h2>
                  <p className="text-sm text-mentor-muted">Bond Mentor is for market awareness and education, not personal financial advice.</p>
                </div>
              </div>
            </div>
          </div>
          <button type="button" onClick={onBackToLogin} className="mt-8 text-sm text-mentor-muted hover:text-mentor-text inline-flex items-center gap-2">
            <ArrowLeft size={16} /> Back to sign in
          </button>
        </section>

        <section className="glass-card p-6 sm:p-8 border border-mentor-border/70">
          <h2 className="text-2xl font-display font-bold mb-2">Request early access</h2>
          <p className="text-sm text-mentor-muted mb-6">Tell us who you are and why Bond Mentor would help you.</p>

          {status && (
            <div className={`mb-5 rounded-2xl border px-4 py-3 text-sm ${status.type === 'success' ? 'border-green-400/30 bg-green-400/10 text-green-100' : 'border-red-400/30 bg-red-400/10 text-red-100'}`}>
              {status.message}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="form-label">First name<input className="form-input" value={form.firstName} onChange={(e) => updateField('firstName', e.target.value)} required /></label>
              <label className="form-label">Last name<input className="form-input" value={form.lastName} onChange={(e) => updateField('lastName', e.target.value)} required /></label>
            </div>
            <label className="form-label">Email<input type="email" className="form-input" value={form.email} onChange={(e) => updateField('email', e.target.value)} required /></label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="form-label">City<input className="form-input" value={form.city} onChange={(e) => updateField('city', e.target.value)} /></label>
              <label className="form-label">State<input className="form-input" value={form.state} onChange={(e) => updateField('state', e.target.value)} /></label>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <label className="form-label">Investor type
                <select className="form-input" value={form.investorType} onChange={(e) => updateField('investorType', e.target.value)}>
                  <option value="individual_investor">Individual investor</option>
                  <option value="advisor">Advisor</option>
                  <option value="student">Student</option>
                  <option value="researcher">Researcher</option>
                  <option value="business">Business</option>
                </select>
              </label>
              <label className="form-label">Investing experience
                <select className="form-input" value={form.investorExperience} onChange={(e) => updateField('investorExperience', e.target.value)}>
                  <option value="novice">Novice</option>
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                  <option value="expert">Expert</option>
                </select>
              </label>
              <label className="form-label">Bond knowledge
                <select className="form-input" value={form.bondKnowledge} onChange={(e) => updateField('bondKnowledge', e.target.value)}>
                  <option value="novice">Novice</option>
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                  <option value="expert">Expert</option>
                </select>
              </label>
            </div>
            <label className="form-label">How did you hear about us?<input className="form-input" value={form.heardFrom} onChange={(e) => updateField('heardFrom', e.target.value)} /></label>
            <label className="form-label">Why are you interested in Bond Mentor?<textarea className="form-input min-h-[120px]" value={form.interestReason} onChange={(e) => updateField('interestReason', e.target.value)} required /></label>

            <input className="hidden" tabIndex="-1" autoComplete="off" value={form.website} onChange={(e) => updateField('website', e.target.value)} aria-hidden="true" />

            <label className="flex gap-3 text-sm text-mentor-muted"><input type="checkbox" checked={form.educationAcknowledged} onChange={(e) => updateField('educationAcknowledged', e.target.checked)} required /> I understand Bond Mentor is an educational market-awareness tool, not personal financial advice.</label>
            <label className="flex gap-3 text-sm text-mentor-muted"><input type="checkbox" checked={form.termsAccepted} onChange={(e) => updateField('termsAccepted', e.target.checked)} required /> I agree that my application can be reviewed for early-access vetting.</label>
            <label className="flex gap-3 text-sm text-mentor-muted"><input type="checkbox" checked={form.marketingConsent} onChange={(e) => updateField('marketingConsent', e.target.checked)} /> Send me Bond Mentor launch and early-access updates.</label>

            <button type="submit" disabled={submitting} className="btn-primary w-full justify-center disabled:opacity-60">
              {submitting ? 'Submitting...' : 'Join Waitlist'}
            </button>
          </form>
        </section>
      </div>
    </div>
  );
}
